import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import Navbar from "@/components/Navbar";
import { createProjectBattle, getEntity, search as searchApi } from "@/lib/api";
import { entityLabel } from "@/lib/format";
import { toast } from "sonner";

const DURATION_OPTIONS = [
  { label: "6 hours", hours: 6 },
  { label: "24 hours", hours: 24 },
  { label: "3 days", hours: 72 },
  { label: "7 days", hours: 168 },
];

export default function CreateBattlePage() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const challengerId = params.get("challenger") || "";
  const [challenger, setChallenger] = useState(null);
  const [q, setQ] = useState("");
  const [results, setResults] = useState([]);
  const [target, setTarget] = useState(null);
  const [title, setTitle] = useState("");
  const [durationHours, setDurationHours] = useState(24);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!challengerId) return;
    getEntity(challengerId)
      .then(setChallenger)
      .catch(() => setError("Challenger entity not found"));
  }, [challengerId]);

  useEffect(() => {
    if (q.trim().length < 1) {
      setResults([]);
      return;
    }
    let cancelled = false;
    searchApi(q)
      .then((rows) => {
        if (!cancelled) {
          setResults((rows || []).filter((e) => e.id !== challenger?.id && e.category === (challenger?.category || "tokens")));
        }
      })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [q, challenger]);

  const canSubmit = useMemo(
    () => !!challenger?.id && !!target?.id && !submitting,
    [challenger, target, submitting],
  );

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!canSubmit) return;
    setSubmitting(true);
    setError(null);
    try {
      const battle = await createProjectBattle({
        challenger_entity_id: challenger.id,
        target_entity_id: target.id,
        title: title.trim() || undefined,
        duration_hours: durationHours,
      });
      toast.success("Battle created");
      navigate(`/battle/${battle.slug}`);
    } catch (err) {
      const detail = err?.response?.data?.detail || err?.message || "Could not create Battle";
      setError(typeof detail === "string" ? detail : "Could not create Battle");
      toast.error(typeof detail === "string" ? detail : "Could not create Battle");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen grain">
      <Navbar category={challenger?.category === "callers" ? "callers" : "tokens"} />
      <main className="mx-auto max-w-lg px-4 py-8">
        <Link to="/battles" className="text-xs text-zinc-500 hover:text-white">← Battles</Link>
        <h1 className="mt-4 font-display text-3xl font-black tracking-tight">Start a Battle</h1>
        <p className="mt-2 text-sm text-zinc-400">
          Challenge another {challenger?.category === "callers" ? "caller" : "token"} with Verified Backing only.
          Creating a Battle does not change rank.
        </p>

        {!challengerId && (
          <div className="mt-6 rounded-xl border border-white/10 px-4 py-4 text-sm text-zinc-400">
            Open this form from a claimed project page.
          </div>
        )}

        {challenger && (
          <form onSubmit={onSubmit} className="mt-8 space-y-5" data-testid="create-battle-form">
            <div>
              <div className="text-[10px] uppercase tracking-wider text-zinc-600">Challenger</div>
              <div className="mt-1 font-display text-xl font-black">{entityLabel(challenger)}</div>
            </div>

            <div>
              <label className="text-[10px] uppercase tracking-wider text-zinc-600">Opponent</label>
              <input
                data-testid="create-battle-search"
                value={q}
                onChange={(e) => setQ(e.target.value)}
                placeholder="Search opponent"
                className="mt-1 w-full rounded-xl border border-white/10 bg-white/5 px-3 py-3 text-sm outline-none focus:border-white/25"
              />
              {!!results.length && (
                <div className="mt-2 overflow-hidden rounded-xl border border-white/10">
                  {results.slice(0, 8).map((e) => (
                    <button
                      key={e.id}
                      type="button"
                      data-testid={`create-battle-pick-${e.slug}`}
                      onClick={() => { setTarget(e); setQ(entityLabel(e)); setResults([]); }}
                      className="block w-full px-3 py-2 text-left text-sm hover:bg-white/5"
                    >
                      {entityLabel(e)}
                    </button>
                  ))}
                </div>
              )}
              {target && (
                <div className="mt-2 text-sm text-gold" data-testid="create-battle-target">
                  vs {entityLabel(target)}
                </div>
              )}
            </div>

            <div>
              <label className="text-[10px] uppercase tracking-wider text-zinc-600">Title (optional)</label>
              <input
                data-testid="create-battle-title"
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                maxLength={160}
                className="mt-1 w-full rounded-xl border border-white/10 bg-white/5 px-3 py-3 text-sm outline-none focus:border-white/25"
              />
            </div>

            <div>
              <label className="text-[10px] uppercase tracking-wider text-zinc-600">Duration</label>
              <select
                data-testid="create-battle-duration"
                value={durationHours}
                onChange={(e) => setDurationHours(Number(e.target.value))}
                className="mt-1 w-full rounded-xl border border-white/10 bg-white/5 px-3 py-3 text-sm outline-none"
              >
                {DURATION_OPTIONS.map((o) => (
                  <option key={o.hours} value={o.hours}>{o.label}</option>
                ))}
              </select>
            </div>

            {error && <div className="text-sm text-negative" data-testid="create-battle-error">{error}</div>}

            <button
              type="submit"
              disabled={!canSubmit}
              data-testid="create-battle-submit"
              className="w-full rounded-full bg-white py-3 font-display text-sm font-black uppercase tracking-wide text-black disabled:opacity-40"
            >
              {submitting ? "Creating…" : "Confirm Battle"}
            </button>
          </form>
        )}
      </main>
    </div>
  );
}
